import { NgTemplateOutlet } from '@angular/common';
import { 
  Signal,
  OnInit,
  computed,
  Component, 
  ChangeDetectionStrategy,
  ContentChildren, effect,
  inject, input, model, output, QueryList, DestroyRef, untracked, ViewEncapsulation,
} from '@angular/core';

/**
 * libs
 */
import { TranslatePipe } from '@ngx-translate/core';

/**
 * enums
 */
import { IThemeColorVariant } from '@core/enums/theme-colors-variants';
import { ITableActionMode, ITableBorder, ITableResponsiveBreakpoint } from '../../enums';

/**
 * interfaces
 */
import {
  ITableAction,
  ILazyLoadEvent,
  ISelectionChangeEvent,
  ITableSortMeta,
  ITableSortChangeEvent,
  ITableColumnUnion,
  ITableColumnsUnion,
  ITableColumnActions,
  TableSortMode,
} from '../../interfaces';
import { IDropdownItem } from '@sharedComponents/dropdown/interfaces/IDropdownItem';

/**
 * utilities
 */
import { TableUtils } from '../../utilities/table-utils';

/**
 * services
 */
import { TableStateService } from '../../services/table-state.service';

/**
 * directives
 */
import { ButtonDirective } from '@sharedDirectives/button/button.directive';
import { BlockUiDirective } from '@shared/directives/block-ui/block-ui.directive';
import { TemplateDirective } from '@sharedDirectives/templates/template.directive';
import { WavesEffectDirective } from '@sharedDirectives/node-waves/waves-effect.directive';
import { TableContentRenderedDirective, TableSortableColumnDirective } from '../../directives';

/**
 * components
 */
import { TableHeaderCheckboxComponent } from '../table-header-checkbox';
import { TableCheckboxComponent } from '../../components/table-checkbox';
import { SelectComponent } from '@forms/components/select/select.component';
import { TableSearchComponent } from '../table-search/table-search.component';
import { TableSortIconComponent } from '../table-sort-icon/table-sort-icon.component';
import { DropdownComponent } from '@sharedComponents/dropdown/dropdown.component';
import { PaginationComponent } from '@sharedComponents/pagination/pagination.component';
import { SpinKitWaveComponent } from '@sharedComponents/spinner/spinkit/components/spin-kit-wave/spin-kit-wave.component';

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  exportAs: 'tableApi',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Cells are usually authored in a consuming component's thead/tbody
  // templates, so they carry that component's style scope. Disabling
  // encapsulation lets the cell rules below reach them. Every selector in
  // table.component.scss is namespaced under `app-table` to stay contained.
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgTemplateOutlet,
    /**
     * libs
     */
    TranslatePipe,
    /**
     * directives
     */
    ButtonDirective,
    BlockUiDirective,
    WavesEffectDirective,
    TableContentRenderedDirective,
    TableSortableColumnDirective,
    /**
     * components
     */
    SelectComponent,
    DropdownComponent,
    PaginationComponent,
    SpinKitWaveComponent,
    TableSearchComponent,
    TableSortIconComponent,
    TableCheckboxComponent,
    TableHeaderCheckboxComponent,
  ],
  providers: [TableStateService]
})
export class TableComponent implements OnInit {

  /**
   * Inject TableStateService
   */
  public readonly tableStateService = inject(TableStateService);

  /**
   * Inject DestroyRef
   */
  public readonly destroyRef = inject(DestroyRef);

  /**
   * Query templates using TemplateDirective
   */
  @ContentChildren(TemplateDirective)
  public templateDirectives?: QueryList<TemplateDirective>;

  // ===========================
  //  start table styles configuration
  // ===========================
  
  /**
   * Responsive configuration for the table
   */
  public responsive = input<boolean | ITableResponsiveBreakpoint | ITableResponsiveBreakpoint[]>(true);

  /**
   * Computed responsive class based on responsive input
   */
  public readonly responsiveClass = computed(() => {
    return TableUtils.generateResponsiveClass(this.responsive());
  });

  /**
   * Width applied to any column that does not declare its own `width`.
   * Pass `null` to leave unsized columns to the browser, as before.
   * @default '170px'
   */
  public defaultColumnWidth = input<string | null>('170px');

  /**
   * Resolve the width for a column: its own `width`, else the table default.
   *
   * The returned value is bound to `min-width`, `width` and `max-width`
   * together. Setting all three to the same value is what pins a column under
   * the default `table-layout: auto` — `max-width` alone is ignored on a
   * `th`/`td`, and `width` alone is only a hint the content can override.
   *
   * @param column - The column being rendered
   * @returns The CSS width, or null to leave the column unsized
   */
  public getColumnWidth(column: ITableColumnUnion): string | null {
    return column.width ?? this.defaultColumnWidth();
  }

  /**
   * Resolved width per column id, exposed to custom `tbodyTemplate` markup so
   * hand-written cells can pin themselves from the column config instead of
   * repeating literal widths:
   *
   * ```html
   * <ng-template appTemplate="tbodyTemplate" let-row let-widths="columnWidths">
   *   <td [style.width]="widths.name"
   *       [style.min-width]="widths.name"
   *       [style.max-width]="widths.name">{{ row.name }}</td>
   * </ng-template>
   * ```
   */
  public readonly columnWidths = computed<Record<string, string | null>>(() => {

    // table-level fallback for columns without their own width
    const defaultWidth = this.defaultColumnWidth();

    return Object.fromEntries(
      this.visibleColumns().map(column => [column.id, column.width ?? defaultWidth]),
    );
  });

  /**
   * Whether the caption block is rendered at all.
   *
   * Distinct from projecting an empty `captionTemplate`: that still renders
   * the `.table_caption` wrapper and its padding. Turning this off also
   * removes the default `<app-table-search />`, since the search box lives
   * inside the caption.
   * @default true
   */
  public showCaption = input<boolean>(true);

  /**
   * Whether the `<thead>` element is rendered at all.
   * @default true
   */
  public showHeader = input<boolean>(true);

  /**
   * Whether the `<tfoot>` element is rendered at all.
   *
   * The default footer shows the "showing N of total" summary, so tables that
   * present a single record rather than a list usually want this off.
   * @default true
   */
  public showFooter = input<boolean>(true);

  /**
   * Caption CSS classes
   */
  public captionClassStyle = input<string | string[]>('');

  /**
   * Whether caption should appear above the table.
   * The caption is rendered outside the responsive scroll wrapper,
   * so its position is controlled by render order, not by `.caption-top`.
   */
  public captionTop = input<boolean>(true);

  /**
   * Table CSS classes
   */
  public tableClassStyle = input<string | string[]>('');

  /**
   * Table color variant
   */
  public tableVariant = input<IThemeColorVariant | null>(null);

  /**
   * Whether table rows are striped
   */
  public tableStriped = input<boolean>(false);

  /**
   * Whether table columns are striped
   */
  public tableStripedColumns = input<boolean>(false);

  /**
   * Whether table rows have hover effect
   */
  public tableHover = input<boolean>(false);

  /**
   * Table border configuration
   */
  public tableBorder = input<boolean | ITableBorder | null>(null);

  /**
   * Whether table is small size
   */
  public tableSmall = input<boolean>(false);

  /**
   * Computed table class styles
   */
  public readonly buildTableClassStyle = computed(() => {

    // init
    const classList: string[] = [];

    // add table class style
    const tableClassStyle = this.tableClassStyle();

    // add table class style to class list
    if (tableClassStyle) {
      if (typeof tableClassStyle === 'string') {
        classList.push(tableClassStyle);
      } else if (Array.isArray(tableClassStyle)) {
        classList.push(tableClassStyle.join(' '));
      }
    }

    // add table small class style
    if (this.tableSmall()) classList.push('table-sm');

    // add table striped class style
    if (this.tableStriped()) classList.push('table-striped');

    // add table striped columns class style
    if (this.tableStripedColumns()) classList.push('table-striped-columns');

    // add table hover class style
    if (this.tableHover()) classList.push('table-hover');

    // add table bordered class style
    const tableBorder = this.tableBorder();
    if (tableBorder != null) {
      if (typeof tableBorder === 'string') {
        classList.push(`table-${tableBorder}`);
      } else if (tableBorder === true) {
        classList.push('table-bordered');
      } else {
        classList.push('table-borderless');
      }
    }
    
    // add table variant class style
    const tableVariant = this.tableVariant();
    if (tableVariant) {
      const variantClassStyle = TableUtils.generateVariantClass(tableVariant);
      if (variantClassStyle) classList.push(variantClassStyle);
    }

    // join the class list and filter out any null values
    return classList.filter(Boolean).join(' ');
  });

  /**
   * Table head color variant
   */
  public tableHeadVariant = input<IThemeColorVariant | null>(null);

  /**
   * Computed table head variant class style
   */
  public readonly tableHeadVariantClassStyle = computed(() => {
    const variant = this.tableHeadVariant();
    return variant ? TableUtils.generateVariantClass(variant) : null;
  });

  /**
   * Table body color variant
   */
  public tableBodyVariant = input<IThemeColorVariant | null>(null);

  /**
   * Whether table body has group divider
   */
  public tableBodyGroupDivider = input<boolean>(false);

  /**
   * Computed table body class styles
   */
  public readonly buildTableBodyClassStyle = computed(() => {

    // init
    const classList: string[] = [];

    // add table body variant class style
    const tableBodyVariant = this.tableBodyVariant();
    if (tableBodyVariant) {
      const variantClassStyle = TableUtils.generateVariantClass(tableBodyVariant);
      if (variantClassStyle) classList.push(variantClassStyle);
    } 

    // add table body group divider class style
    if (this.tableBodyGroupDivider()) classList.push('table-group-divider');

    // return the class list
    return classList.filter(Boolean).join(' ');

  });

  // ===========================
  //  start table controls configuration
  // ===========================

  /**
   * Table data (required input) - for local mode (array data)
   */
  public value = input<any[]>([]);

  /**
   * Table columns configuration (required input)
   */
  public columns = input<ITableColumnsUnion>([]);

  /**
   * Table sort mode
   * @default 'single'
   */
  public sortMode = input<TableSortMode>('single');

  /**
   * Default sort metadata applied once during initialization.
   * - In single mode: only first valid item is applied.
   * - In multiple mode: all valid items are applied by priority order.
   */
  public defaultSortMeta = input<ITableSortMeta[]>([]);

  /**
   * Whether to show pagination
   */
  public paginator = input<boolean>(true);

  /**
   * Two-way binding for current page (model signal)
   * Can be set from parent: [(currentPageModel)]="myValue"
   */
  public currentPageModel = model<number>(1);

  /**
   * Two-way binding for rows per page (model signal)
   * Can be set from parent: [(rowsPerPageModel)]="myValue"
   * Or changed by user via dropdown selector
   */
  public rowsPerPageModel = model<number>(10);

  /**
   * Rows per page options for selector dropdown
   */
  public rowsPerPageOptions = input<number[]>([5, 10, 20, 50, 100]);

  /**
   * Whether to show rows per page selector next to pagination
   */
  public showRowsPerPageSelector = input<boolean>(false);

  /**
   * Whether to show selection column
   * @default true
   */
  public selectable = input<boolean>(true);

  /**
   * Property name for selection identifier
   * @default 'id'
   */
  public selectionKey = input<any>('id');

  // ===========================
  //  Output Events
  // ===========================

  /**
   * Output event emitted when table is initialized
   */
  public onInit = output<ILazyLoadEvent>();

  /**
   * Output event emitted when a row is selected
   */
  public onRowSelect = output<ISelectionChangeEvent>();

  /**
   * Output event emitted when a row is unselected
   */
  public onRowUnselect = output<ISelectionChangeEvent>();

  /**
   * Output event emitted when selection changes (any row is selected or unselected)
   */
  public onSelectionChange = output<ISelectionChangeEvent>();

  /**
   * Output event emitted when table needs to load data (PrimeNG-style)
   * Emits when page or rowsPerPage changes in lazy mode
   * Parent component should listen to this event and call loadData() with the response
   */
  public onLazyLoad = output<ILazyLoadEvent>();

  /**
   * Output event emitted when sorting state changes.
   * Works for both single and multiple sort modes.
   */
  public onSort = output<ITableSortChangeEvent>();

  /**
   * Whether to enable lazy loading mode
   * When true, emits onLazyLoad event instead of using value array
   * @default false
   */
  public lazy = input<boolean>(false);

  // ===========================
  //  Middleware - Service Access
  // ===========================

  /**
   * Get current page from service (middleware)
   */
  public get currentPage(): Signal<number> {
    return this.tableStateService.currentPage;
  };

  /**
   * Get visible columns from service (middleware)
   */
  public get visibleColumns() {
    return this.tableStateService.visibleColumns 
  };

  /**
   * Get paginated data from service (middleware)
   */
  public get paginatedData(): Signal<any[]> {
    return this.tableStateService.paginatedData;
  };

  /**
   * Get total records from service (middleware)
   */
  public get totalRecords(): Signal<number> {
    return this.tableStateService.totalRecords;
  };

  /**
   * Get rows per page from service (middleware)
   */
  public get rowsPerPage(): Signal<number> {
    return this.tableStateService.rowsPerPage;
  };

  /**
   * Get table data from service (middleware)
   */
  public get tableData(): Signal<any[]> {
    return this.tableStateService.value;
  };

  /**
   * Get loading state from service (middleware)
   */
  public get loading(): Signal<boolean> {
    return this.tableStateService.loading;
  };

  /**
   * Get error state from service (middleware)
   */
  public get error(): Signal<any> {
    return this.tableStateService.error;
  };

  /**
   * Get frazzled state from service (middleware)
   */
  public get frazzled(): Signal<boolean> {
    return this.tableStateService.frazzled;
  };

  /**
   * Get selected rows from service (middleware)
   */
  public get selectedRows(): Signal<any[]> {
    return this.tableStateService.selectedRows;
  };

  /**
   * Get inline actions from service (middleware)
   */
  public get inlineActions(): Signal<ITableAction[]> {
    return this.tableStateService.inlineActions;
  };

  /**
   * Get menu actions from service (middleware)
   */
  public get menuActions(): Signal<ITableAction[]> {
    return this.tableStateService.menuActions;
  };

  /**
   * Whether {@link defaultSortMeta} has already been pushed into the state
   * service. Held on the instance rather than in the constructor closure
   * because {@link _initSnapshotSyncState} applies it ahead of the effect.
   */
  private _isDefaultSortApplied = false;

  /**
   * Constructor - sync component inputs and models with service
   */
  constructor() {
    let isFirstSortEffectRun = true;
    let isFirstLazyLoadEffectRun = true;

    // Sync value input with service (only when lazy is false)
    effect(() => {
      const lazyMode = this.lazy();
      if (!lazyMode) {
        const tableValue = this.value();
        this.tableStateService.setValue(tableValue);
      }
    });

    // Sync columns input with service
    effect(() => {
      const columns = this.columns();
      this.tableStateService.setColumns(columns);
    });

    // Sync sort mode with service
    effect(() => {
      const sortMode = this.sortMode();
      this.tableStateService.setSortMode(sortMode);
    });

    // Apply default sort once when valid meta is provided.
    //
    // `_initSnapshotSyncState` usually gets there first — this effect is the
    // fallback for a `defaultSortMeta` binding that only resolves after init.
    effect(() => {
      const defaultSortMeta = this.defaultSortMeta();

      if (this._isDefaultSortApplied) return;
      if (!Array.isArray(defaultSortMeta) || defaultSortMeta.length === 0) return;

      this._applyDefaultSortMeta();
    });

    // Sync rowsPerPageModel with service
    effect(() => {
      const rows = this.rowsPerPageModel();
      this.tableStateService.setRowsPerPage(rows);
    });

    // Sync currentPageModel with service
    effect(() => {
      const page = this.currentPageModel();
      this.tableStateService.setCurrentPage(page);
    });

    // ...and mirror the service's own page resets (search, rows-per-page,
    // out-of-range clamp) back into the model. Without this the two drift
    // apart, and `onPageChange` — which writes through the model — goes silent
    // for whichever page number the stale model still holds.
    effect(() => {
      const page = this.currentPage();

      untracked(() => {
        if (this.currentPageModel() !== page) this.currentPageModel.set(page);
      });
    });

    // Sync selectionKey with service
    effect(() => {
      const key = this.selectionKey();
      this.tableStateService.setSelectionKey(key);
    });

    // Sync lazy mode with service
    effect(() => {
      const lazyMode = this.lazy();
      this.tableStateService.setLazy(lazyMode);
    });

    // Sync selection change event with service
    effect(() => {
      const selectedRows = this.selectedRows()
      this.onSelectionChange.emit(this._generateSelectionChangeEventData(selectedRows));
    });


    // Emit lazy load event when page or rowsPerPage changes in lazy mode
    effect(() => {

      const lazyMode = this.lazy();
      if (!lazyMode) return;

      const currentPage = this.currentPage();
      const rowsPerPage = this.rowsPerPage();
      const searchValue = this.tableStateService.searchValueWithOperatorsPipe();
      const sortMode = this.tableStateService.sortMode();
      const sortMeta = this.tableStateService.sortMeta();

      // Skip the initial constructor-run emission — `onInit` already covers
      // the first load; only react to actual page/search/sort changes here.
      if (isFirstLazyLoadEffectRun) {
        isFirstLazyLoadEffectRun = false;
        return;
      }

      this._emitLazyLoadEvent();

    });

    // Emit sort change event whenever sort state changes.
    effect(() => {
      const sortMeta = this.tableStateService.sortMeta();
      const sortMode = this.tableStateService.sortMode();
      
      // Skip initial constructor-run emission.
      if (isFirstSortEffectRun) {
        isFirstSortEffectRun = false;
        return;
      }

      const primarySort = sortMeta[0];

      this.onSort.emit({
        column: primarySort?.column ?? null,
        order: primarySort?.order ?? null,
        sortMeta,
        sortMode,
      });
    });

  }

  /**
   * @lifecycle ngOnInit
   */
  ngOnInit(): void {
    this._initSnapshotSyncState();
    this._emitOnInitEvent();
  }

  /**
   * Push the current input/model values into the state service synchronously,
   * before `onInit` is emitted.
   *
   * The constructor effects that normally keep these in sync only flush on the
   * first change detection pass — which happens *after* `ngOnInit`. Lazy
   * consumers load their first page from the `onInit` event, so without this
   * snapshot that request reads the service defaults and a binding such as
   * `[rowsPerPageModel]="20"` would still fetch a page of 10.
   *
   * Add any further state that must be seeded ahead of the `onInit` emission
   * here. Order matters where setters have side effects.
   */
  private _initSnapshotSyncState(): void {

    // rows per page first: `setRowsPerPage` resets the current page back to 1,
    // so seeding it after the page would discard the bound page value.
    this.tableStateService.setRowsPerPage(this.rowsPerPageModel());

    // columns before the sort state: `sortColumn()` resolves a column's
    // `sortField` out of this list, so an empty one would send the view-model
    // field name as `sort_column` on the very first request.
    this.tableStateService.setColumns(this.columns());

    // sort mode before the default meta, since applying the meta slices it
    // according to the mode. `setSortMeta` also resets the page, so both must
    // land before the page below.
    this.tableStateService.setSortMode(this.sortMode());
    this._applyDefaultSortMeta();

    this.tableStateService.setCurrentPage(this.currentPageModel());
  }

  /**
   * Push {@link defaultSortMeta} into the state service, once.
   *
   * In `single` mode only the first rule is kept; in `multiple` mode all of
   * them are, by priority order.
   */
  private _applyDefaultSortMeta(): void {

    const defaultSortMeta = this.defaultSortMeta();

    // nothing to apply — leave the table unsorted
    if (!Array.isArray(defaultSortMeta) || defaultSortMeta.length === 0) return;

    const finalDefaultSortMeta =
      this.sortMode() === 'single' ? defaultSortMeta.slice(0, 1) : defaultSortMeta;

    this.tableStateService.setSortMeta(finalDefaultSortMeta);
    this._isDefaultSortApplied = true;
  }

  /**
   * Method to emit on init event
   */
  private _emitOnInitEvent(): void {
    this.onInit.emit({
      component: this,
      state: this.tableStateService,
    });
  }

  /**
   * Private method to emit lazy load event
   */
  private _emitLazyLoadEvent(): void {

    const event: ILazyLoadEvent = {
      component: this,
      state: this.tableStateService,
    };

    this.onLazyLoad.emit(event);
  }

  /**
   * Method to handle pagination page change
   */
  public onPageChange(page: number): void {
    
    // void
    if (this.currentPage() === page) return;

    this.currentPageModel.set(page);
  }

  /**
   * Method to handle rows per page change
   */
  public onRowsPerPageChange(rows: any): void {
    this.rowsPerPageModel.set(rows as number);
  }

  /**
   * Method to handle select all rows
   */
  public onSelectAllRows(rows: any[]): void {
    this.tableStateService.setSelectedRows(rows);
  }

  /**
   * Method to handle unselect all rows
   */
  public onUnselectAllRows(rows: any[]): void {
    this.tableStateService.removeSelectedRows(rows);
  }

  /**
   * Method to handle toggle all rows
   */
  public onToggleAllRows(rows: any[], selected: unknown): void {

    const isSelected = selected as boolean;

    this.tableStateService.onToggleAllRows(rows, isSelected);
  }

  /**
   * Method to select a row
   */
  public selectRow(row: any): void {

    try {
      this.tableStateService.selectRow(row);
    } 
    catch (error) {
      throw error;
    }
  }

  /**
   * Method to unselect a row
   */
  public unselectRow(row: any): void {

    try {
      this.tableStateService.unselectRow(row);
    } 
    catch (error) {
      throw error;
    }

  }

  /**
   * Method to toggle a row selection
   */
  public onRowToggle(row: any, selected: unknown): void {
    this.tableStateService.onRowToggle(row, selected);
  }

  /**
   * Method to check if a row is selected
   */
  public isRowSelected(row: any): boolean {
    return this.tableStateService.isRowSelected(row);
  }

  /**
   * Method to check if all current page rows are selected
   */
  public isAllCurrentPageRowsSelected(): boolean {
    return this.tableStateService.isAllCurrentPageRowsSelected();
  }
  

  /**
   * Method to generate selection change event data
   * @param row - The row that caused the selection change
   * @returns The selection change event data
   */
  private _generateSelectionChangeEventData(row: any): ISelectionChangeEvent {
    return {
      row: row,
      selectedRows: this.selectedRows(),
      selectedCount: this.selectedRows().length,
    }
  }

  // ===========================
  //  Action Methods
  // ===========================
  
  public isColumnActions<TContext>(column: ITableColumnUnion<TContext>) {
    return TableUtils.isColumnActions<TContext>(column);
  }

  /**
   * Check if action is visible (delegates to TableUtils)
   * @param action - The action to check
   * @param row - The row data
   * @returns True if action should be visible
   */
  public isActionVisible(action: ITableAction, row: any): boolean {
    return TableUtils.isActionVisible(action, row);
  }

  /**
   * Check if action is disabled (delegates to TableUtils)
   * @param action - The action to check
   * @param row - The row data
   * @returns True if action should be disabled
   */
  public isActionDisabled(action: ITableAction, row: any): boolean {
    return TableUtils.isActionDisabled(action, row);
  }

  /**
   * Get action mode (delegates to TableUtils)
   * @param action - The action to get mode for
   * @returns The action mode
   */
  public getActionMode(action: ITableAction): ITableActionMode {
    return TableUtils.getActionMode(action);
  }

  /**
   * Execute action command (delegates to TableUtils)
   * @param action - The action to execute
   * @param row - The row data
   */
  public executeAction(action: ITableAction, row: any): void {
    TableUtils.executeAction(action, row);
  }

  /**
   * Get actions filtered by mode (delegates to TableUtils)
   * @param actions - Array of actions
   * @param row - The row data
   * @param mode - The mode to filter by
   * @returns Filtered actions that match the mode
   */
  public getActionsByMode(actions: ITableAction[], row: any, mode: ITableActionMode): ITableAction[] {
    return TableUtils.getActionsByMode(actions, row, mode);
  }

  /**
   * Get column dropdown options
   * @param column - The column to get options for
   * @returns The dropdown options
   */
  public getColumnDropdownOptions(column: ITableColumnActions): ITableColumnActions['dropdownOptions'] {
    return column.dropdownOptions ?? {};
  }

  /**
   * Convert table actions to dropdown items for menu rendering
   * @param actions - Array of table actions
   * @param row - The row data
   * @returns Array of dropdown items
   */
  public convertActionsToDropdownItems(actions: ITableAction[], row: any): IDropdownItem[] {
    return actions
            .filter(action => this.isActionVisible(action, row))
            .map(action => ({
              id: action.id,
              label: action.label,
              icon: action.icon,
              classStyle: action.classStyle,
              disabled: this.isActionDisabled(action, row),
              command: () => this.executeAction(action, row),
            }));
  }

  /**
   * Get template by name
   * 
   * @param name - The name of the template to get
   * @returns The template directive or null if not found
   */
  public getTemplate(name: string): TemplateDirective | null {

    // find template
    if (!this.templateDirectives) return null;

    // find template
    return this.templateDirectives.find(template => template.name === name) ?? null;

  }

}